import React from 'react';
import { UsersIcon, VideoIcon, ShieldCheckIcon, EyeIcon, ClockIcon, GlobeIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
import { Link } from 'react-router-dom';
const features = [{
  icon: UsersIcon,
  title: 'Small groups',
  text: '8–10 children per session for real participation and connection.',
  bg: 'bg-sage-100'
}, {
  icon: VideoIcon,
  title: 'Live, interactive',
  text: 'Guided discussion and activities — not passive screen time.',
  bg: 'bg-[#EDF2F7]'
}, {
  icon: ShieldCheckIcon,
  title: 'Safe by design',
  text: 'Moderated sessions with safeguarding protocols built in.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: EyeIcon,
  title: 'Parent visibility',
  text: 'Clear updates, recordings access, and regular feedback for parents.',
  bg: 'bg-sand-100'
}];
const structure = [{
  step: '01',
  title: 'Welcome & connection',
  text: 'Short opening that helps children settle and feel seen.'
}, {
  step: '02',
  title: 'Guided learning block',
  text: 'Age-appropriate lesson with discussion and active participation.'
}, {
  step: '03',
  title: 'Reflection & closing',
  text: 'Brief reflection to anchor what was learned into daily life.'
}];
export function OnlineLearning() {
  return <>
      <SubpageHero eyebrow="Live, safe, and built for modern Muslim families worldwide" title="Online Learning" paragraphs={["Our children's learning happens online so families can access consistent support — wherever they live.", 'We focus on live interaction, small groups, and a respectful environment where every child feels seen, protected, and encouraged.']} primaryCta={{
      label: 'Explore Programs',
      to: '/en/children/kids-programs'
    }} secondaryCta={{
      label: 'Scholarships & Support',
      to: '/en/children/scholarships'
    }} image="https://cdn.magicpatterns.com/uploads/jjNQUzfbm3fVgHUYbPU5fW/online-learning.jpg" imageAlt="Smiling girl in a live online class with classmates on screen" compact masked data-id="element-3336" data-mp="2sk3bjb3dyv0h" />

      {/* Simple structure */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-3337" data-mp="2hxtlj7ibniia">
        <Container data-id="element-3338" data-mp="2hycuvztwoykp">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3339" data-mp="2hvqs0dhyn78t">
            A simple structure children quickly get used to
          </h2>
          <p className="mt-5 text-ink-700 max-w-3xl leading-relaxed" data-id="element-3340" data-mp="2rpha90nhulg2">
            Our model is designed for attention, safety, and real engagement —
            not passive screen time.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-3341" data-mp="1z60wtpaacjgd">
            {structure.map((s, i) => <motion.div key={s.step} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft" data-id="element-3342" data-mp="1rnklm8moa1xu" data-mpi={i}>
                <span className="font-display text-3xl text-sand-400" data-id="element-3343" data-mp="yzyggvxsqp8v">
                  {s.step}
                </span>
                <h3 className="mt-3 font-display text-lg text-ink-900" data-id="element-3344" data-mp="ty95fgakqbks">
                  {s.title}
                </h3>
                <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-3345" data-mp="kznmvl53qwk0">
                  {s.text}
                </p>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* What makes it safe */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3346" data-mp="2p2756s3ay1kz">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3347" data-mp="2bb0zge9fj7pn" />
        <Container data-id="element-3348" data-mp="2ba9cfrtweiaz">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3349" data-mp="2gax0q5qqsl1i">
            What makes our online learning different
          </h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-3350" data-mp="21cl7ya6hpwfm">
            {features.map((f, i) => <motion.div key={f.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl ${f.bg} border border-ink-900/5 p-6 flex gap-4`} data-id="element-3351" data-mp="26ealbwh6qm6v" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700 flex-shrink-0" data-id="element-3352" data-mp="rml07tket4b">
                  <f.icon size={18} strokeWidth={1.6} data-id="element-3353" data-mp="9pf3fkul2gvx" />
                </span>
                <div data-id="element-3354" data-mp="oeurgt2qc87x">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-3355" data-mp="oeb9xdgzrluf">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm leading-relaxed" data-id="element-3356" data-mp="jfriwynjjjv7">
                    {f.text}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Practical info */}
      <section className="py-16 md:py-24 bg-[#F7EDE9]/60" data-id="element-3357" data-mp="2z6nlfmkp1kpb">
        <Container data-id="element-3358" data-mp="2z98mi9d63wm0">
          <div className="grid md:grid-cols-3 gap-5" data-id="element-3359" data-mp="2v63q8ojokr5t">
            {[{
            icon: ClockIcon,
            label: 'Session length',
            value: '45–60 minutes'
          }, {
            icon: GlobeIcon,
            label: 'Time zones',
            value: 'Multiple schedules across regions'
          }, {
            icon: UsersIcon,
            label: 'Group size',
            value: '8–10 children'
          }].map(d => <div key={d.label} className="rounded-2xl bg-white border border-sage-200 p-6 shadow-soft text-center" data-id="element-3360" data-mp="1h3s6zc25dd4w">
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-sage-100 text-forest-700 mx-auto" data-id="element-3361" data-mp="1qtllolxl68dd">
                  <d.icon size={18} strokeWidth={1.6} data-id="element-3362" data-mp="1vs6iebdmq9x2" />
                </span>
                <p className="mt-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-forest-700" data-id="element-3363" data-mp="20tcd2equo798">
                  {d.label}
                </p>
                <p className="mt-2 font-display text-xl text-ink-900" data-id="element-3364" data-mp="3pbowqd6cgkom">
                  {d.value}
                </p>
              </div>)}
          </div>
        </Container>
      </section>

      {/* Ready to begin */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3365" data-mp="3gc1wvvt9jmxo">
        <PatternDecor side="right" y="middle" size="md" data-id="element-3366" data-mp="3bcpb7ee4olwo" />
        <Container data-id="element-3367" data-mp="3be8sn8bit301">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-3368" data-mp="cu0kd9cupo16">
            <div className="max-w-xl" data-id="element-3369" data-mp="7v7273mmd8uo">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3370" data-mp="7w8hjkao4hi7">
                Ready to begin?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-3371" data-mp="3248mvhudzeh">
                Choose a program that matches your child's age and needs — and
                start building confidence, values, and belonging in a safe
                learning space.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3372" data-mp="qsgpivv2f6h7">
              <Link to="/en/children/kids-programs" data-id="element-3373" data-mp="3vc4eu095uim3">
                <Button variant="primary" data-id="element-3374" data-mp="3vab52c393i9v">View Kids Programs</Button>
              </Link>
              <Link to="/en/children/scholarships" data-id="element-3375" data-mp="3xymfrwj9j5r">
                <Button variant="secondary" data-id="element-3376" data-mp="3mbg0f4fuug8o">Apply for a Scholarship</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}